import React, { useState } from 'react';
import axios from 'axios';

function todayStr() {
  return new Date(Date.now() + 9 * 60 * 60 * 1000).toISOString().slice(0, 10);
}
function monthAgoStr() {
  return new Date(Date.now() + 9 * 60 * 60 * 1000 - 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
}

const STATUS_LABELS = {
  sent:          { label: '발송됨',   color: '#60a5fa' },
  delivered:     { label: '수신완료', color: '#34d399' },
  opened:        { label: '열람',     color: '#4ade80' },
  clicked:       { label: '클릭',     color: '#22c55e' },
  bounce:        { label: '반송',     color: '#f87171' },
  hardbounced:   { label: '반송',     color: '#f87171' },
  'soft-bounce': { label: '임시반송', color: '#fb923c' },
  spam:          { label: '스팸',     color: '#ef4444' },
  blocked:       { label: '차단',     color: '#dc2626' },
  unsub:         { label: '수신거부', color: '#9ca3af' },
  deferred:      { label: '지연',     color: '#fdba74' },
};

export default function MailjetHistorySection({ baseUrl, accent = 'amber' }) {
  const [fromDate, setFromDate] = useState(monthAgoStr);
  const [toDate, setToDate]     = useState(todayStr);
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(false);

  const accentCls = {
    amber:  'bg-amber-500/15 text-amber-400 hover:bg-amber-500/25 border border-amber-500/20',
    sky:    'bg-sky-500/15 text-sky-400 hover:bg-sky-500/25 border border-sky-500/20',
    violet: 'bg-violet-500/15 text-violet-400 hover:bg-violet-500/25 border border-violet-500/20',
  }[accent] || 'bg-white/[0.08] text-slate-300 hover:bg-white/[0.12]';

  const query = async () => {
    if (!fromDate || !toDate) return;
    setLoading(true);
    setData(null);
    try {
      const sep = baseUrl.includes('?') ? '&' : '?';
      const res = await axios.get(`${baseUrl}${sep}from=${fromDate}&to=${toDate}`);
      setData(res.data);
    } catch {
      setData([]);
    }
    setLoading(false);
  };

  return (
    <div className="bg-[#111827] border border-white/[0.08] rounded-2xl p-5">
      {/* 헤더 + 날짜 필터 한 줄 */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <h3 className="text-white text-sm font-bold mr-2">기간별 발송 조회</h3>
        <input
          type="date" value={fromDate}
          onChange={e => setFromDate(e.target.value)}
          className="bg-white/[0.05] border border-white/[0.08] rounded-lg px-3 py-1.5 text-xs text-white [color-scheme:dark] focus:outline-none focus:border-white/20"
        />
        <span className="text-slate-500 text-xs">~</span>
        <input
          type="date" value={toDate}
          onChange={e => setToDate(e.target.value)}
          className="bg-white/[0.05] border border-white/[0.08] rounded-lg px-3 py-1.5 text-xs text-white [color-scheme:dark] focus:outline-none focus:border-white/20"
        />
        <button
          onClick={query}
          disabled={loading}
          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors disabled:opacity-50 ${accentCls}`}
        >
          {loading ? '조회중...' : '조회'}
        </button>
        <span className="ml-auto text-xs text-slate-600">Mailjet 직접 조회</span>
      </div>

      {data === null ? (
        <p className="text-xs text-slate-600 text-center py-6">날짜를 선택하고 조회하세요.</p>
      ) : data.length === 0 ? (
        <p className="text-xs text-slate-500 text-center py-6">해당 기간에 발송 이력이 없습니다.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse">
            <colgroup>
              <col className="w-32" />
              <col />
              <col className="w-12" />
              <col className="w-20" />
              <col className="w-12" />
              <col className="w-24" />
            </colgroup>
            <thead>
              <tr className="border-b border-white/[0.06]">
                <th className="text-left text-slate-500 font-medium py-2 pr-3">발송일시</th>
                <th className="text-left text-slate-500 font-medium py-2 pr-3">제목</th>
                <th className="text-center text-slate-500 font-medium py-2 pr-3">발송</th>
                <th className="text-center text-slate-500 font-medium py-2 pr-3">열람</th>
                <th className="text-center text-slate-500 font-medium py-2 pr-3">미열람</th>
                <th className="text-left text-slate-500 font-medium py-2">상태</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/[0.04]">
              {data.map((group, i) => {
                const openedCnt = (group.statusCounts?.opened || 0) + (group.statusCounts?.clicked || 0);
                const notOpenedCnt = (group.statusCounts?.sent || 0) + (group.statusCounts?.delivered || 0);
                const openRate = group.totalCount > 0 ? Math.round(openedCnt / group.totalCount * 100) : 0;
                return (
                  <tr key={i} className="hover:bg-white/[0.02] transition-colors">
                    {/* 발송일시 */}
                    <td className="py-2.5 pr-3 text-slate-400 whitespace-nowrap align-middle">
                      {new Date(group.sentAt).toLocaleString('ko-KR', {
                        timeZone: 'Asia/Seoul',
                        month: '2-digit', day: '2-digit',
                        hour: '2-digit', minute: '2-digit',
                      })}
                    </td>
                    {/* 제목 */}
                    <td className="py-2.5 pr-3 align-middle max-w-0">
                      {group.subject
                        ? <span className="text-white truncate block" title={group.subject}>{group.subject}</span>
                        : <span className="text-slate-600 italic">제목 없음</span>
                      }
                    </td>
                    {/* 발송 수 */}
                    <td className="py-2.5 pr-3 text-center text-slate-300 font-medium tabular-nums align-middle">{group.totalCount}</td>
                    {/* 열람 */}
                    <td className="py-2.5 pr-3 text-center font-medium tabular-nums align-middle">
                      <span className="text-green-400">{openedCnt}</span>
                      {openRate > 0 && <span className="text-slate-600 ml-1">({openRate}%)</span>}
                    </td>
                    {/* 미열람 */}
                    <td className="py-2.5 pr-3 text-center text-slate-400 tabular-nums align-middle">{notOpenedCnt}</td>
                    {/* 상태 뱃지 */}
                    <td className="py-2.5 align-middle">
                      <div className="flex flex-wrap gap-1">
                        {group.bounceCount > 0 && (
                          <span className="px-1.5 py-0.5 rounded bg-red-500/10 text-red-400 whitespace-nowrap">
                            반송 {group.bounceCount}
                          </span>
                        )}
                        {Object.entries(group.statusCounts || {})
                          .filter(([s]) => !['opened','clicked','sent','delivered','bounce','hardbounced','soft-bounce'].includes(s))
                          .map(([status, count]) => {
                            const info = STATUS_LABELS[status];
                            return info ? (
                              <span key={status} style={{ color: info.color }}
                                className="px-1.5 py-0.5 rounded bg-white/[0.04] whitespace-nowrap">
                                {info.label} {count}
                              </span>
                            ) : null;
                          })}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
